/**
 * Mapping between the admin forms and the API payloads.
 *
 * Brands and brand products are sent as multipart/form-data (they carry an
 * image), so lists and nested objects travel as JSON strings — the API's
 * validators accept both that and real JSON bodies. Pages are plain JSON.
 */

import { attachUids, stripUids } from "Helpers/ContentValidation";

/* ------------------------------------------------------------------ brands */

export const emptyBrandForm = () => ({
  name: "",
  slug: "",
  group: "brand",
  badge: "",
  logo_class: "",
  tagline: "",
  description: "",
  intro: [],
  why_title: "",
  why_points: [],
  commitment_title: "",
  commitment: [],
  range_heading: "",
  page_route: "",
  parent_brand: "",
  show_on_all_products: true,
  sort_order: 0,
  status: "active",
  primary_color: "",
  secondary_color: "",
  background_color: "",
  border_color: "",
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  logo_file: null,
});

/** API record -> form values. */
export const brandToFormValues = (brand) => ({
  ...emptyBrandForm(),
  name: brand.name || "",
  slug: brand.slug || "",
  group: brand.group || "brand",
  badge: brand.badge || "",
  logo_class: brand.logo_class || "",
  tagline: brand.tagline || "",
  description: brand.description || "",
  intro: brand.intro || [],
  why_title: brand.why_title || "",
  why_points: brand.why_points || [],
  commitment_title: brand.commitment_title || "",
  commitment: brand.commitment || [],
  range_heading: brand.range_heading || "",
  page_route: brand.page_route || "",
  // The API populates parent_brand on read but expects an id on write.
  parent_brand:
    (typeof brand.parent_brand === "object" && brand.parent_brand !== null
      ? brand.parent_brand._id
      : brand.parent_brand) || "",
  show_on_all_products: brand.show_on_all_products !== false,
  sort_order: brand.sort_order ?? 0,
  status: brand.status || "active",
  primary_color: brand.theme?.primary_color || "",
  secondary_color: brand.theme?.secondary_color || "",
  background_color: brand.theme?.background_color || "",
  border_color: brand.theme?.border_color || "",
  seo_title: brand.seo?.title || "",
  seo_description: brand.seo?.description || "",
  seo_keywords: brand.seo?.keywords || "",
  logo_file: null,
});

/** Form values -> multipart body. */
export const brandFormToFormData = (values) => {
  const data = new FormData();

  data.append("name", values.name.trim());
  if (values.slug?.trim()) data.append("slug", values.slug.trim());
  data.append("group", values.group);
  data.append("badge", values.badge || "");
  data.append("logo_class", values.logo_class || "");
  data.append("tagline", values.tagline || "");
  data.append("description", values.description || "");
  data.append("why_title", values.why_title || "");
  data.append("commitment_title", values.commitment_title || "");
  data.append("range_heading", values.range_heading || "");
  data.append("page_route", values.page_route || "");
  data.append("parent_brand", values.parent_brand || "none");
  data.append("show_on_all_products", values.show_on_all_products ? "true" : "false");
  data.append("sort_order", String(Number(values.sort_order) || 0));
  data.append("status", values.status || "active");

  data.append("intro", JSON.stringify(values.intro || []));
  data.append("why_points", JSON.stringify(values.why_points || []));
  data.append("commitment", JSON.stringify(values.commitment || []));

  data.append(
    "theme",
    JSON.stringify({
      primary_color: values.primary_color || "",
      secondary_color: values.secondary_color || "",
      background_color: values.background_color || "",
      border_color: values.border_color || "",
    })
  );

  data.append(
    "seo",
    JSON.stringify({
      title: values.seo_title || "",
      description: values.seo_description || "",
      keywords: values.seo_keywords || "",
    })
  );

  if (values.logo_file) data.append("logo", values.logo_file);

  return data;
};

/* ---------------------------------------------------------- brand products */

export const emptyBrandProductForm = () => ({
  brand: "",
  name: "",
  variant: "",
  pack_size: "",
  description: "",
  sort_order: 0,
  status: "active",
  image_file: null,
});

export const brandProductToFormValues = (product) => ({
  ...emptyBrandProductForm(),
  brand:
    (typeof product.brand === "object" && product.brand !== null
      ? product.brand._id
      : product.brand) || "",
  name: product.name || "",
  variant: product.variant || "",
  pack_size: product.pack_size || "",
  description: product.description || "",
  sort_order: product.sort_order ?? 0,
  status: product.status || "active",
  image_file: null,
});

export const brandProductFormToFormData = (values) => {
  const data = new FormData();

  data.append("brand", values.brand);
  data.append("name", values.name.trim());
  data.append("variant", values.variant || "");
  data.append("pack_size", values.pack_size || "");
  data.append("description", values.description || "");
  data.append("sort_order", String(Number(values.sort_order) || 0));
  data.append("status", values.status || "active");

  if (values.image_file) data.append("image", values.image_file);

  return data;
};

/* ------------------------------------------------------------------- pages */

export const emptyPageForm = () => ({
  name: "",
  slug: "",
  page_route: "",
  title: "",
  subtitle: "",
  status: "active",
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  sections: [],
});

export const pageToFormValues = (page) => ({
  ...emptyPageForm(),
  name: page.name || "",
  slug: page.slug || "",
  page_route: page.page_route || "",
  title: page.title || "",
  subtitle: page.subtitle || "",
  status: page.status || "active",
  seo_title: page.seo?.title || "",
  seo_description: page.seo?.description || "",
  seo_keywords: page.seo?.keywords || "",
  // Sections get a client-only id so the builder can reorder them safely.
  sections: attachUids(page.sections || []),
});

export const pageFormToPayload = (values) => ({
  name: values.name.trim(),
  ...(values.slug?.trim() ? { slug: values.slug.trim() } : {}),
  page_route: values.page_route || "",
  title: values.title || "",
  subtitle: values.subtitle || "",
  status: values.status || "active",
  seo: {
    title: values.seo_title || "",
    description: values.seo_description || "",
    keywords: values.seo_keywords || "",
  },
  sections: stripUids(values.sections || []),
});
